import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { makeStyles, createStyles } from '@bb-ui/react-library';
import { Tabs } from '@bb-ui/react-library/dist/components/Tabs';
import { Tab } from '@bb-ui/react-library/dist/components/Tab';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { useTranslation } from 'react-i18next';
import { AppRouteProps } from 'routes/AppRoute';
import { useAppRouteContext } from 'contexts/AppRouteContext';
import { useAuthorization } from 'hooks/useAuthorization';
import { PageTabProps } from 'components/PageTabs/PageTabs.types';

export interface GroupedTabsProps {
  tabPanelId: string;
  tabProps?: PageTabProps;
  disabledTabIds?: string[];
}

interface TabGroup {
  label: string;
  i18nKey: string;
  routes: string[];
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: 0,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
    },
    sectionLabel: {
      textTransform: 'uppercase',
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '0.08em',
      color: 'rgba(0, 0, 0, 0.48)',
      marginBottom: '5px',
    },
    groupSeparator: {
      width: '1px',
      height: '24px',
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
      margin: '0 20px',
      alignSelf: 'center',
    },
    tabs: {
      minHeight: '36px',
      overflow: 'visible',
      '& .MuiTabs-indicator': {
        display: 'none !important',
        height: '0 !important',
        backgroundColor: 'transparent !important',
      },
      '& .MuiTabs-flexContainer': {
        gap: '2px',
      },
    },
    tab: {
      minHeight: '32px',
      minWidth: 'unset',
      padding: '4px 12px',
      fontSize: '13px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.50)',
      textTransform: 'none',
      transition: 'all 0.15s ease',
      borderRadius: '6px',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.035)',
      },
      '&.Mui-selected': {
        fontWeight: 600,
        color: 'rgba(0, 0, 0, 0.92)',
        backgroundColor: 'rgba(0, 0, 0, 0.07)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
      },
    },
  }),
);

export const GroupedTabs: React.FunctionComponent<GroupedTabsProps> = (props) => {
  const PageTab = Tab as React.ElementType<PageTabProps>;
  const { tabPanelId, tabProps, disabledTabIds } = props;
  const { currentRoute, currentLevelRoutes } = useAppRouteContext();
  const { hasPermission } = useAuthorization();
  const routeParams: { [key: string]: string } = useParams();
  const { t } = useTranslation();
  const classes = useStyles();

  // Define tab groups - matching by route path ending
  const tabGroups: TabGroup[] = [
    {
      label: 'Overview',
      i18nKey: 'groupedTabs.overview',
      routes: ['/information', '/assets', '/display'],
    },
    {
      label: 'Access & Security',
      i18nKey: 'groupedTabs.accessSecurity',
      routes: ['/auth', '/groups', '/licenses', '/flags'],
    },
    {
      label: 'Infrastructure',
      i18nKey: 'groupedTabs.infrastructure',
      routes: [
        '/release-stage',
        '/configuration',
        '/mobileconfiguration',
        '/jobs',
        '/executions',
        '/shared-rds',
      ],
    },
    {
      label: 'Monitoring',
      i18nKey: 'groupedTabs.monitoring',
      routes: ['/inventory', '/logs', '/dda'],
    },
  ];

  // Filter available tabs based on authorization and disabled state
  const tabRoutes: AppRouteProps[] = currentLevelRoutes.filter((route) => {
    if (!route.hasTab) {
      return false;
    }

    if (
      route.authorization &&
      !route.authorization.every(({ permission, service }) => hasPermission(service, permission))
    ) {
      return false;
    }

    if (disabledTabIds && disabledTabIds.includes(route.path)) {
      return false;
    }

    return true;
  });

  // Group tabs by their category
  const groupedRoutes = tabGroups.map((group) => {
    const routes = tabRoutes.filter((route) =>
      group.routes.some((groupRoute) => route.path.endsWith(groupRoute)),
    );
    return { ...group, routes };
  });

  // Find ungrouped tabs and add to the first group
  const allGroupedPaths = groupedRoutes.flatMap((g) => g.routes.map((r) => r.path));
  const ungroupedRoutes = tabRoutes.filter((route) => !allGroupedPaths.includes(route.path));

  if (ungroupedRoutes.length > 0 && groupedRoutes.length > 0) {
    groupedRoutes[0].routes = [...ungroupedRoutes, ...groupedRoutes[0].routes];
  }

  // Interpolate route params into links
  function interpolateParams(value: string) {
    return Object.keys(routeParams).reduce(
      (result, param: string) =>
        result.replace(new RegExp(`/:${param}(/|$)`, 'g'), `/${routeParams[param]}$1`),
      value,
    );
  }

  // Determine current tab value for each group
  const getCurrentTabValue = (routes: AppRouteProps[]) => {
    if (!currentRoute) return 0;

    const tabIndex = routes.indexOf(currentRoute);
    if (tabIndex !== -1) return tabIndex;

    // Check for nested route matching
    const parentRoute = routes.filter((route) => currentRoute.path.startsWith(route.path)).pop();

    return parentRoute ? routes.indexOf(parentRoute) : 0;
  };

  return (
    <div className={classes.root} data-testid="grouped-tabs">
      {groupedRoutes
        .filter((group) => group.routes.length > 0)
        .map((group, groupIndex) => {
          const currentTab = getCurrentTabValue(group.routes);
          const hasActiveTab = group.routes.some(
            (route) =>
              currentRoute?.path === route.path || currentRoute?.path.startsWith(`${route.path}/`),
          );

          return (
            <React.Fragment key={groupIndex}>
              {groupIndex > 0 && <div className={classes.groupSeparator} />}
              <div className={classes.section}>
                <Typography variant="body2" className={classes.sectionLabel}>
                  {t(group.i18nKey) || group.label}
                </Typography>
                <Tabs
                  value={hasActiveTab ? currentTab : false}
                  variant="scrollable"
                  className={classes.tabs}
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  data-testid={`grouped-tabs-${group.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {group.routes.map((route) => (
                    <PageTab
                      aria-controls={tabPanelId}
                      component={Link}
                      id={`tab-${route.path}`}
                      key={`tab-${route.path}`}
                      label={
                        route.icon ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            {route.icon}
                            {t(route.name!)}
                          </span>
                        ) : (
                          t(route.name!)
                        )
                      }
                      to={interpolateParams(route.path)}
                      className={classes.tab}
                      {...tabProps}
                    />
                  ))}
                </Tabs>
              </div>
            </React.Fragment>
          );
        })}
    </div>
  );
};

export default GroupedTabs;
